import { useEffect, useState } from 'react';
import { BadgeIndianRupee, CalendarDays, ChevronRight, Phone, Sparkles } from 'lucide-react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { formatRupees, loadContact, loadMyTransactions, loadPlans, parseIsoDate } from '../lib/subscribe';
import type { PlanCourse, SoftConfig, TransactionRow } from '../lib/subscribe';
import { Badge, Button, EmptyState, LoadingState } from '../components/ui';

export function StudentSubscribe() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const selectedCourseId = searchParams.get('courseId');
  const [plans, setPlans] = useState<PlanCourse[]>();
  const [contact, setContact] = useState<SoftConfig>();
  const [latest, setLatest] = useState<TransactionRow[]>();

  useEffect(() => {
    let active = true;
    void Promise.all([loadPlans(), loadContact().catch(() => undefined), loadMyTransactions(3).catch(() => [] as TransactionRow[])])
      .then(([planRows, softConfig, transactions]) => {
        if (!active) return;
        setPlans(planRows);
        setContact(softConfig);
        setLatest(transactions);
      });
    return () => { active = false; };
  }, []);

  const visiblePlans = plans?.filter((plan) => !selectedCourseId || plan.objectId === selectedCourseId);

  return <div className="tests-page">
    <section className="page-title-row"><div><h1>{selectedCourseId ? 'Purchase options' : location.pathname === '/batches' ? 'Batches' : 'Subscription plans'}</h1><p>{selectedCourseId ? 'Review the catalog price and contact AyurGuide to subscribe.' : 'Browse course batches and their subscription options.'}</p></div><BadgeIndianRupee size={22} /></section>
    {!plans ? <LoadingState /> : visiblePlans?.length
      ? <div className="plan-grid">{visiblePlans.map((plan) => <PlanCard key={plan.objectId} plan={plan} />)}</div>
      : <EmptyState title={selectedCourseId ? 'No purchase plan configured' : 'No paid plans yet'} description={selectedCourseId ? 'This course does not have a published paid plan. Contact AyurGuide for current purchase details.' : 'Course plans appear here as soon as they are published.'} />}
    <ContactCard contact={contact} />
    <section className="course-section">
      <div className="section-heading"><h2>Recent payments</h2>{latest?.length ? <Link className="text-button" to="/transactions">View all<ChevronRight size={14} /></Link> : undefined}</div>
      {latest?.length ? <TransactionList rows={latest} compact /> : null}
    </section>
  </div>;
}

function PlanCard({ plan }: { plan: PlanCourse }) {
  const price = Number(plan.Price ?? 0);
  const mrp = Number(plan.Mrp ?? 0);
  const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const durationMonths = Number(plan.Duration ?? 0);
  const [phone, setPhone] = useState('');
  useEffect(() => { let active = true; void loadContact().then((config) => { if (active && config?.ContactNo) setPhone(config.ContactNo); }).catch(() => undefined); return () => { active = false; }; }, []);
  return <article className="plan-card">
    <header className="plan-head">
      <Badge tone="amber"><Sparkles size={12} /> Plan</Badge>
      {discount > 0 && <span className="plan-discount">{discount}% off</span>}
    </header>
    <h3>{plan.CourseTitle}</h3>
    {plan.CourseSubTitle && <small className="plan-sub">{plan.CourseSubTitle}</small>}
    {durationMonths > 0 && <span className="plan-duration"><CalendarDays size={13} /> {durationMonths} month{durationMonths === 1 ? '' : 's'} access</span>}
    <footer className="plan-pricing">
      <strong>{formatRupees(price)}</strong>
      {mrp > price && <s>{formatRupees(mrp)}</s>}
      <Button variant="soft" onClick={() => { if (phone) window.location.href = `tel:${phone}`; }}>
        {phone ? 'Call to subscribe' : 'How to subscribe'}
      </Button>
    </footer>
  </article>;
}

export function ContactCard({ contact }: { contact?: SoftConfig }) {
  if (!contact?.ContactNo) return null;
  return <section className="contact-card">
    <div className="contact-icon"><Phone size={20} /></div>
    <div><strong>Offline payment</strong><p>{contact.Msg || 'Please contact on below number for offline payment -'}</p></div>
    <a href={`tel:${contact.ContactNo}`}><Button variant="primary" icon={Phone}>{contact.ContactNo}</Button></a>
  </section>;
}

export function TransactionList({ rows, compact = false }: { rows: TransactionRow[]; compact?: boolean }) {
  return <div className="utility-list txn-list">{rows.map((row) => {
    const course = row.CoursePtr && typeof row.CoursePtr === 'object' ? (row.CoursePtr as { CourseTitle?: string }) : undefined;
    const date = parseIsoDate(row.TxnDate ?? row.StartDate);
    const status = String(row.TxnStatus ?? '');
    return <div className="utility-row" key={row.objectId}>
      <span><BadgeIndianRupee size={17} /></span>
      <div><strong>{course?.CourseTitle ?? 'Subscription'}</strong><small>{date ? date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</small></div>
      {!compact && <div className="txn-dates"><small>{fmt(row.StartDate)} → {fmt(row.EndDate)}</small></div>}
      <span className="utility-status">{formatRupees(row.Price)}</span>
      <Badge tone={status.toLowerCase() === 'success' ? 'green' : status ? 'gray' : 'gray'}>{status || 'Pending'}</Badge>
    </div>;
  })}</div>;
}

function fmt(value: unknown) {
  const date = parseIsoDate(value);
  return date ? date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
}
